import * as React from 'react'
import { useMinimumWageQuery } from '../useMinimumWageQuery'
import { MinimalWagePageProps } from '../types'
import { useMinimumWageMutation } from '../useMinimumWageMutation'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { MinimumWageCountryLayout } from '../components/MinimumWageCountryLayout'
import {
  FieldName,
  MinimumWageOptionsSection,
} from '../containers/MinimumWageOptionsSection'
import { CalculationStartDateSection } from '../containers/CalculationStartDateSection'
import { MinimumWagePolishOptionsSection } from '../containers/MinimumWagePolishOptionsSection'
import { GenericMinimumWageExpandableItemSection } from '../containers/GenericMinimumWageExpandableItemSection'
import { COUNTRY_TABS } from '../config'
import { useNavigate } from 'react-router-dom'
import { GenericMinimumWageConfigurationModel } from '@/app/models/configuration/minimumWage/genericMinimumWageConfigurationModel'

import { PreLoader } from '../components/PreLoader'
import { MinimumWageConfigurationModel } from '@/app/models/configuration/minimumWageConfigurationModel'
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils'

const defaultFields: FieldName[] = [
  'printCalculatedHours',
  'calculateCabotageAndCrossTrade',
]

const allowedCurrenciesInner = ['EUR']

const allowedCurrencies = ['EUR']

const translateKey = 'genericConfiguration'

export const GenericMinimumWageSettingsPage = ({
  match,
}: MinimalWagePageProps) => {
  const { companyId, country } = match.params
  const stateKey = `${country}Configuration`
  const q = useMinimumWageQuery(companyId)
  const up = useMinimumWageMutation(companyId)
  const navigate = useNavigate()
  const { t } = useLocalize()

  const [genericState, setGenericState] = React.useState<MinimumWageConfigurationModel|null>(null)

  const state = genericState && stateKey in genericState ? genericState[stateKey] : null

  const setState: React.Dispatch<
    React.SetStateAction<GenericMinimumWageConfigurationModel | null>
  > = (value) => {
    setGenericState((prev) => ({
      ...prev,
      [stateKey]:
        typeof value === 'function'
          ? (
              value as (
                prevState: GenericMinimumWageConfigurationModel | null,
              ) => GenericMinimumWageConfigurationModel | null
            )(prev[stateKey] ?? null)
          : value,
    }))
  }

  React.useEffect(() => {
    setGenericState(null)
  }, [companyId])

  React.useEffect(() => {
    if (q.data) {
      const newState = structuredClone(q.data)
      setGenericState(newState)
    }
  }, [q.data, stateKey])

  const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('genericConfiguration', t), [t])
  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    configuration: t('configuration.minimumWages.options'),
    expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
  }), [t])

  if (!state || !q.data) {
    return <PreLoader />
  }

  const isDirty = JSON.stringify(state) !== JSON.stringify(q.data[stateKey])

  const selectedCountry = COUNTRY_TABS.find((c) => c.name === country)

  if (!selectedCountry || selectedCountry.nationId === -1) {
    navigate(
      `/et/company/${companyId}/configuration/minimum-wage`,
    )
    return null
  }

  return (
    <MinimumWageCountryLayout
      selectedCountryValue={country}
      isDirty={isDirty}
      handleSave={() => up.mutateAsync({ ...q.data, [stateKey]: state })}
      handleCancel={() => setState(q.data[stateKey])}
      dataTestId="generic-minimum-wage-settings-page"
      isLoading={q.isFetching || up.isPending}
      diffBefore={q.data[stateKey] as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
      diffLabels={diffLabels}
      diffRenderers={diffRenderers}
    >
      {selectedCountry && (
        <CalculationStartDateSection state={state} setState={setState} />
      )}

      {selectedCountry && (
        <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
          <GenericMinimumWageExpandableItemSection
            state={state}
            setState={setState}
          />

          <MinimumWageOptionsSection
            className="tw-my-5"
            loading={false}
            fields={defaultFields}
            translateKey={translateKey}
            state={state}
            accommodationAndAllowanceMaxDate={
              q.data.generalConfiguration.accommodationAndAllowanceMaxDate
            }
            setState={setState}
          />

          <MinimumWagePolishOptionsSection
            className="tw-my-5"
            loading={false}
            state={state}
            setState={setState}
          />
        </div>
      )}

      <div style={{ minHeight: '90px' }} />
    </MinimumWageCountryLayout>
  )
}
